import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const REASON_CODES = ["defective", "not_as_described", "not_delivered", "missing_item", "other"];

/**
 * Buyer-facing modal to request a refund on a paid order. Renders inline over the
 * page and calls POST /api/orders/{id}/refund-request. When it succeeds, the
 * parent gets a fresh copy of the order (with `refund_request` block) via `onDone`.
 */
export default function RefundRequestModal({ order, onClose, onDone }) {
  const { t } = useLang();
  const [reasonCode, setReasonCode] = useState("");
  const [comment, setComment] = useState("");
  const [payoutDetails, setPayoutDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!reasonCode) return toast.error(t("refund.err_reason"));
    if (reasonCode === "other" && !comment.trim()) return toast.error(t("refund.err_other_comment"));
    setSubmitting(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/refund-request`, {
        reason_code: reasonCode,
        comment: comment.trim(),
        payout_details: payoutDetails.trim(),
      });
      toast.success(t("refund.submit_success"));
      onDone?.(data.order);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const commentRequired = reasonCode === "other";

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="refund-modal-backdrop"
    >
      <div
        className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="refund-modal"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-[#F5F5F0]"
          data-testid="refund-modal-close"
          aria-label={t("refund.modal_close")}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 pt-6 pb-4 border-b border-[#1A3322]/10">
          <p className="text-[11px] tracking-[0.15em] uppercase text-[#C85A32] font-bold">
            #{order.id.slice(0, 8)} · {order.total?.toFixed(2)} €
          </p>
          <h2 className="heading font-extrabold text-2xl text-[#1A3322] mt-1">
            {t("refund.modal_title")}
          </h2>
          <p className="text-sm text-[#4B5563] mt-2">{t("refund.modal_intro")}</p>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div className="space-y-1">
            <Label>{t("refund.reason_label")}</Label>
            <Select value={reasonCode} onValueChange={setReasonCode}>
              <SelectTrigger data-testid="refund-reason-select">
                <SelectValue placeholder={t("refund.reason_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                {REASON_CODES.map((code) => (
                  <SelectItem key={code} value={code} data-testid={`refund-reason-${code}`}>
                    {t(`refund.reason_${code}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>
              {commentRequired ? t("refund.comment_label_required") : t("refund.comment_label")}
            </Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("refund.comment_placeholder")}
              rows={4}
              data-testid="refund-comment"
            />
          </div>

          <div className="space-y-1">
            <Label>{t("refund.payout_label")}</Label>
            <Input
              value={payoutDetails}
              onChange={(e) => setPayoutDetails(e.target.value)}
              placeholder={t("refund.payout_placeholder")}
              data-testid="refund-payout"
            />
            <p className="text-[11px] text-[#4B5563]">{t("refund.payout_hint")}</p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
              className="rounded-full"
              data-testid="refund-cancel"
            >
              {t("refund.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="pill-btn bg-[#C85A32] hover:bg-[#D98A2C] rounded-full"
              data-testid="refund-submit"
            >
              {submitting ? t("refund.submitting") : t("refund.submit")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
